import React, { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FiFilm, FiHome, FiPlus, FiShoppingBag, FiUser } from 'react-icons/fi';
import { useCommunity } from '../../contexts/CommunityContext';
import { BuyProvider } from '../../contexts/BuyContext';
import { useLanguage } from '../../i18n';
import CartSidebar from '../CartSidebar';
import BuyAccountModal from './BuyAccountModal';
import CommunityNav from './CommunityNav';
import CommunityAuthModal from './CommunityAuthModal';
import FloatingCart from './FloatingCart';
import { BLUE_GRADIENT, BLUE } from './brand';

/** Shell of the community platform: navbar + content + footer + mobile bottom nav. */
export default function CommunityLayout() {
  const { t } = useLanguage();
  const { user, unread, openAuth } = useCommunity();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // The community is the first page of the storefront — give the tab the brand
  // name while a community page is open and restore the old title on exit.
  useEffect(() => {
    const previous = document.title;
    document.title = 'CodeKhmerLearning Community';
    return () => { document.title = previous; };
  }, []);

  // Phone bottom bar: Home · Reel · Create · Shop · Profile — the raised “+”
  // stays in the middle. Messages and notifications live in the top bar.
  const items = [
    { to: '/', icon: <FiHome className="w-6 h-6" />, label: t('articleNav'), exact: true },
    { to: '/reels', icon: <FiFilm className="w-6 h-6" />, label: t('reelNav') },
    { write: true, icon: <FiPlus className="w-7 h-7" />, label: t('writePost') },
    { to: '/shop', icon: <FiShoppingBag className="w-6 h-6" />, label: t('shopNav') },
    { to: user ? '/me' : '#', icon: <FiUser className="w-6 h-6" />, label: t('profileNav'), badge: user ? unread.notifications : 0, auth: !user },
  ];

  return (
    <BuyProvider>
      <div className="community-page min-h-screen flex flex-col">
      <div className="h-1 w-full" style={{ background: BLUE_GRADIENT }} />
      <CommunityNav />

      <main className="flex-1 pb-24 md:pb-0">
        <div key={pathname} className="page-anim">
          <Outlet />
        </div>
      </main>

      <footer className="hidden md:block border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-[1500px] px-3 lg:px-4 py-6 text-xs text-gray-500 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL}/ckl.png`} alt="CodeKhmerLearning" className="w-6 h-6" />
            <span>© {new Date().getFullYear()} {t('communityTitle')}</span>
          </div>
          <nav className="flex items-center gap-4 font-semibold">
            <Link to="/" className="hover:underline">{t('articleNav')}</Link>
            <Link to="/shop" className="hover:underline">{t('shopNav')}</Link>
            <Link to="/create-shop" className="hover:underline">{t('startYourShop')}</Link>
            <Link to="/service" className="hover:underline">{t('servicePage')}</Link>
          </nav>
        </div>
      </footer>

      {/* Phone bottom bar — 5 clear slots with the write button raised in the
          middle (cleaner on small screens than cramming 6 items in). */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800 shadow-[0_-4px_20px_rgba(0,0,0,.06)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="grid grid-cols-5 max-w-md mx-auto">
          {items.map((item) => {
            if (item.write) {
              return (
                <button
                  key="write"
                  type="button"
                  onClick={() => (user ? navigate(`/?write=1&t=${Date.now()}`) : openAuth('signin', t('loginRequiredDesc')))}
                  className="relative flex flex-col items-center justify-end pb-1.5"
                  aria-label={item.label}
                >
                  <span
                    className="w-12 h-12 -mt-5 rounded-full flex items-center justify-center text-white ring-4 ring-white dark:ring-gray-900 shadow-lg"
                    style={{ background: BLUE }}
                  >
                    {item.icon}
                  </span>
                  <span className="text-[11px] font-semibold mt-0.5" style={{ color: BLUE }}>
                    {item.label}
                  </span>
                </button>
              );
            }
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            if (item.auth) {
              return (
                <button
                  key="profile"
                  type="button"
                  onClick={() => openAuth('signin', t('loginRequiredDesc'))}
                  className="flex flex-col items-center justify-center gap-1 pt-2.5 pb-1.5 text-gray-500 dark:text-gray-400"
                >
                  {item.icon}
                  <span className="text-[11px] font-semibold">{item.label}</span>
                </button>
              );
            }
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative flex flex-col items-center justify-center gap-1 pt-2.5 pb-1.5 transition ${
                  active ? '' : 'text-gray-500 dark:text-gray-400'
                }`}
                style={active ? { color: BLUE } : undefined}
              >
                {active && (
                  <span className="absolute top-0 w-10 h-0.5 rounded-b-full" style={{ background: BLUE }} />
                )}
                <span className="relative">
                  {item.icon}
                  {item.badge > 0 && (
                    <span
                      className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-gray-900"
                      style={{ background: '#ef4444' }}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </span>
                <span className="text-[11px] font-semibold">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <CommunityAuthModal />

      {/* Cart: the pinned icon appears as soon as something is in it; the drawer
          groups the lines per shop, and adding from the community first offers the
          “use your account to buy” Google prompt. */}
      <FloatingCart />
      <CartSidebar />
      <BuyAccountModal />
      </div>
    </BuyProvider>
  );
}
